define('core/media',[
    'underscore',

    'core/strings',

    'core/models/Media',
    'core/models/RichMediaViewModel',
    'core/views/ImageRichMediaView',
    'core/views/IframeRichMediaView',
    'core/views/FacebookPhotoRichMediaView',
    'core/views/AutoplayRichMediaView',
    'core/views/TwitterRichMediaView',
    'core/views/SoundCloudRichMediaView',
    'core/views/VineRichMediaView',
    'core/views/IframeGifRichMediaView',
], function (
    _,

    strings,

    MediaModel,
    RichMediaViewModel,
    ImageRichMediaView,
    IframeRichMediaView,
    FacebookPhotoRichMediaView,
    AutoplayRichMediaView,
    TwitterRichMediaView,
    SoundCloudRichMediaView,
    VineRichMediaView,
    IframeGifRichMediaView
) {
    'use strict';

    /* Rich Media

     * Each rich media object is associated with a link in the user's post.

     * There is a user setting to hide / show media; this is stored in the settings
     * model below as 'collapsed' (true === hide media). Each RichMediaView then
     * has a 'deferred' attribute which is the inverse (true === show media).

     * When a post is rendered, rich media links are replaced with RichMediaLinkViews.
     * In deferred mode this looks identical to the original link, but in collapsible
     * mode it provides additional information about the media (ex. title) and
     * serves as an expand / collapse button.

     * When deferred, the RichMediaView displays a placeholder and waits for the
     * user to scroll close to the view. This is done by registering with the
     * deferred views system in Lounge and waiting for the viewport:enter event.
     * Until that event happens, a placeholder is displayed; if there is an issue
     * with the deferred views system the user can click the placeholder.

     * When collapsible, the RichMediaView is hidden. When the RichMediaLinkView
     * is clicked it will display its content and autoplay (if possible).

     * Until it is displayed, the RichMediaView sets its size to the expected
     * media size. This is done by measuring the available space and scaling the
     * dimensions returned by getMediaDimensions. Some media types don't define
     * both dimensions (ex. SoundCloud will fill any width but has a height
     * requirement) so the dimensions are optional.
    */

    var gettext = strings.get;

    // Common, easily-translated actions
    var PROVIDER_MESSAGES = {
        PLAY_HIDE: {
            kind: 'html',
            providerExpandMessage: gettext('Play'),
            providerCollapseMessage: gettext('Hide'),
        },
        VIEW_HIDE: {
            kind: 'html',
            providerExpandMessage: gettext('View'),
            providerCollapseMessage: gettext('Hide'),
        },
        VIEW_IMAGE: {
            kind: 'image',
            providerIcon: 'icon-images',
            providerExpandMessage: gettext('View'),
            providerCollapseMessage: gettext('Hide'),
        },
    };

    var getRichMediaViewConfig = function (model) {
        // Create an instance of the correct RichMediaView subclass based on
        // the media model attributes.

        var extendMessage = function (name, icon) {
            return _.defaults({
                providerIcon: icon,
            }, PROVIDER_MESSAGES[name]);
        };

        var providerConstant = null;
        var Cls = null;

        var PROVIDER = MediaModel.MEDIA_TYPES;
        switch (model.get('mediaType')) {
        // Images
        case PROVIDER.IMAGE:
        case PROVIDER.IMAGE_UPLOAD:
            providerConstant = PROVIDER_MESSAGES.VIEW_IMAGE;
            break;
        case PROVIDER.FACEBOOK_PHOTO:
            Cls = FacebookPhotoRichMediaView;
            providerConstant = PROVIDER_MESSAGES.VIEW_IMAGE;
            break;
        // gif videos
        case PROVIDER.GIF_VIDEO:
            Cls = IframeGifRichMediaView;
            providerConstant = PROVIDER_MESSAGES.VIEW_HIDE;
            break;
        // Other
        case PROVIDER.VIMEO_VIDEO:
        case PROVIDER.YOUTUBE_VIDEO:
            Cls = AutoplayRichMediaView;
            providerConstant = extendMessage('PLAY_HIDE', 'icon-video');
            break;
        case PROVIDER.TWITTER_STATUS:
            Cls = TwitterRichMediaView;
            providerConstant = extendMessage('VIEW_HIDE', 'icon-twitter');
            break;
        case PROVIDER.VINE_VIDEO:
            Cls = VineRichMediaView;
            providerConstant = extendMessage('PLAY_HIDE', 'icon-video');
            break;
        case PROVIDER.FACEBOOK_VIDEO:
            providerConstant = extendMessage('VIEW_HIDE', 'icon-video');
            break;
        case PROVIDER.SOUNDCLOUD_SOUND:
            Cls = SoundCloudRichMediaView;
            providerConstant = extendMessage('PLAY_HIDE', 'icon-music');
            break;
        case PROVIDER.GOOGLE_MAP:
            providerConstant = extendMessage('VIEW_HIDE', 'icon-map');
            break;
        // Invalid rich media type
        default:
            return null;
        }

        // Generic views
        if (Cls === null) {
            switch (providerConstant.kind) {
            case 'webpage':
                // Webpage support has been removed
                return null;
            case 'html':
                Cls = IframeRichMediaView;
                break;
            case 'image':
                Cls = ImageRichMediaView;
                break;
            }
        }

        // Initialize model
        var mediaViewModel = new RichMediaViewModel(providerConstant);

        return {
            Cls: Cls,
            mediaViewModel: mediaViewModel,
        };
    };

    var instantiateRichMediaView = function (model) {
        var config = getRichMediaViewConfig(model);

        if (!config)
            return null;

        // Instantiate the view
        return new config.Cls({
            model: config.mediaViewModel,
            media: model,
        });
    };

    var instantiateRichMediaThumbnail = function (model) {
        return new ImageRichMediaView({
            model: new RichMediaViewModel(PROVIDER_MESSAGES.VIEW_IMAGE),
            media: model,
        });
    };

    return {
        instantiateRichMediaView: instantiateRichMediaView,
        instantiateRichMediaThumbnail: instantiateRichMediaThumbnail,
        getRichMediaViewConfig: getRichMediaViewConfig,
    };
});

